import { MAX_DATE_RANGE_YEARS, MAX_HISTORICAL_YEARS } from "@/common/report.common";
import { DateRangePreset } from "@/types/report.types";
import { ReportDateValidationInput, DateValidationResult } from "@/utils/date.utils";
import { startOfDay, subYears, addYears } from "date-fns";

export const validateUniqueEmails = (emails: string[]): { isValid: boolean; errorKey?: string } => {
  const seenEmails = new Map<string, boolean>();
  
  for (const email of emails) {
    const lowerCaseEmail = email.toLowerCase();
    
    if (seenEmails.has(lowerCaseEmail)) {
      return {
        isValid: false,
        errorKey: "generateReport.error.duplicateEmail"
      };
    }
    
    seenEmails.set(lowerCaseEmail, true);
  }
  
  return { isValid: true };
};/**
 * Validates the custom date range for the report generation form.
 * Returns an object indicating validity and potential error details.
 *
 * Rules:
 * - If preset is 'custom', startDate and endDate are required.
 * - startDate cannot be after endDate.
 * - startDate cannot be more than 3 years in the past.
 * - Date range (endDate - startDate) cannot exceed 1 year.
 */

export const validateReportCustomDateRange = (
  data: ReportDateValidationInput
): DateValidationResult => {
  if (data.dateRangePreset !== DateRangePreset.CUSTOM) {
    return { isValid: true }; // No validation needed if not custom
  }

  if (!data.startDate) {
    return {
      isValid: false,
      errorKey: "errors.required",
      path: ["startDate"],
    };
  }
  if (!data.endDate) {
    return {
      isValid: false,
      errorKey: "errors.required",
      path: ["endDate"],
    };
  }

  // Both dates are present, proceed with range checks
  if (data.startDate > data.endDate) {
    return {
      isValid: false,
      errorKey: "generateReport.error.invalidDateSelection",
      path: ["endDate"],
    };
  }

  const threeYearsAgo = startOfDay(subYears(new Date(), MAX_HISTORICAL_YEARS));
  if (data.startDate < threeYearsAgo) {
    return {
      isValid: false,
      errorKey: "generateReport.error.startDateTooFarPast", // Params like { years: 3 } should be added during translation
      path: ["startDate"],
    };
  }

  const oneYearAfterStartDate = addYears(data.startDate, MAX_DATE_RANGE_YEARS);
  if (data.endDate > oneYearAfterStartDate) {
    return {
      isValid: false,
      errorKey: "generateReport.error.dateRangeTooLong", // Params like { years: 1 } should be added during translation
      path: ["endDate"],
    };
  }

  return { isValid: true }; // All checks passed for custom range
};
 